<template>
  <div class="archivio-container">
    <h1>Gestione Assistenza Tecnica Risposti</h1>

    <!-- Selezione intervallo date -->
    <div class="date-filters">
      <label for="startDate">Data Inizio</label>
      <input
        type="date"
        v-model="startDate"
        id="startDate"
      />

      <label for="endDate">Data Fine</label>
      <input
        type="date"
        v-model="endDate"
        id="endDate"
      />
    </div>

    <!-- Pulsante di ricerca -->
    <div class="search-buttons">
      <button @click="searchDocuments" :disabled="isLoading">
        Cerca documenti
      </button>
    </div>

    <!-- Selezione filtri -->
    <div class="filters">
      <div>
        <label for="filterStudio">Filtro Studio</label>
        <select v-model="selectedStudio" id="filterStudio">
          <option value="">Seleziona Studio (tutti)</option>
          <option value="Tivoli Quartier generale">Tivoli Quartier generale</option>
          <option value="FF Genova">FF Genova</option>
          <option value="FF Bologna">FF Bologna</option>
          <option value="FF Bari">FF Bari</option>
          <option value="FF Pescara">FF Pescara</option>
          <option value="FF Firenze">FF Firenze</option>
          <option value="FF Torino">FF Torino</option>
          <option value="FF Napoli">FF Napoli</option>
          <option value="FF Milano">FF Milano</option>
          <option value="FF Milano Sempione">FF Milano Sempione</option>
          <option value="FF Milano Città Studi">FF Milano Città Studi</option>
          <option value="FF Roma">FF Roma</option>
          <option value="FF Roma Eur">FF Roma Eur</option>
          <option value="FF Salerno">FF Salerno</option>
          <option value="FF Cagliari">FF Cagliari</option>
          <option value="FF Padova">FF Padova</option>
          <option value="undefined">Nessuno Studio</option>
        </select>
      </div>

      <div>
        <label for="filterType">Filtro Tipo Conversazione</label>
        <select v-model="selectedType" id="filterType">
          <option value="">Tutti</option>
          <option value="Trainer">Trainer</option>
          <option value="Richiesta">Richiesta</option>
        </select>
      </div>
    </div>

    <!-- Loading Spinner -->
    <div v-if="isLoading" class="loader">
      <p>Caricamento in corso...</p>
    </div>

    <template v-else>
      <div v-if="foundRows.length" class="summary-box">
        <p>
          Nel periodo selezionato, risposte backend (<strong>type = Trainer</strong>):
          <strong>{{ trainerMessagesCount }}</strong> messaggi
          su <strong>{{ trainerUniqueUsersCount }}</strong> utenti
          (<strong>{{ trainerUniqueTicketsCount }}</strong> ticket unici).
        </p>
      </div>

      <div v-if="trainerByResponder.length" class="operators-box">
        <h3>Ripartizione risposte per operatore</h3>
        <table class="operators-table">
          <thead>
            <tr>
              <th>Email operatore</th>
              <th>Nome operatore</th>
              <th>ID operatore</th>
              <th>Messaggi Trainer</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, idx) in trainerByResponder" :key="`${item.responderKey}-${idx}`">
              <td>{{ item.responderEmail || 'N/D' }}</td>
              <td>{{ item.responderName || 'N/D' }}</td>
              <td>{{ item.responderId || 'N/D' }}</td>
              <td>{{ item.count }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Contatore righe filtrate -->
      <p v-if="filteredRows.length">
        Righe trovate: <strong>{{ filteredRows.length }}</strong>
      </p>

      <!-- Nessun risultato -->
      <div v-else>
        <p>Nessun documento trovato per l'intervallo selezionato (o con i filtri attuali).</p>
      </div>
    </template>

    <!-- Tabella risultati -->
    <div v-if="filteredRows.length && !isLoading">
      <table>
        <thead>
          <tr>
            <th>ID Documento</th>
            <th>Tipo Conversazione</th>
            <th>ID Utente</th>
            <th>Nome Utente</th>
            <th>Email Utente</th>
            <th>Studio</th>
            <th>ID Operatore</th>
            <th>Email Operatore</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in filteredRows" :key="index">
            <td>{{ row.docId }}</td>
            <td>{{ row.type }}</td>
            <td>{{ row.userId }}</td>
            <td>{{ row.userName || 'N/D' }}</td>
            <td>{{ row.userEmail || 'N/D' }}</td>
            <td>{{ row.userStudio || 'N/D' }}</td>
            <td>{{ row.responderId || 'N/D' }}</td>
            <td>{{ row.responderEmail || 'N/D' }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import {
  getFirestore,
  collection,
  getDocs,
  query,
  where,
  documentId
} from "firebase/firestore";

export default {
  name: "AssistenzaTecnicaFilter",
  data() {
    return {
      startDate: "",
      endDate: "",
      isLoading: false,

      // Righe totali (documento+conversazione) trovate dopo la ricerca
      foundRows: [],

      // Filtri
      selectedStudio: "",
      selectedType: "",

      // Cache utenti: evita letture duplicate tra ricerche consecutive
      usersCache: {}
    };
  },
  computed: {
    trainerRows() {
      return this.foundRows.filter(row => this.normalizeType(row.type) === "trainer");
    },
    trainerMessagesCount() {
      return this.trainerRows.length;
    },
    trainerUniqueUsersCount() {
      return new Set(
        this.trainerRows.map(row => row.userId).filter(Boolean)
      ).size;
    },
    trainerUniqueTicketsCount() {
      return new Set(
        this.trainerRows.map(row => row.docId).filter(Boolean)
      ).size;
    },
    trainerByResponder() {
      const grouped = new Map();

      this.trainerRows.forEach(row => {
        const responderId = String(row.responderId || "").trim();
        const responderEmail = String(row.responderEmail || "").trim().toLowerCase();
        const responderName = String(row.responderName || "").trim();
        const responderKey = responderEmail || responderId || "sconosciuto";

        if (!grouped.has(responderKey)) {
          grouped.set(responderKey, {
            responderKey,
            responderId,
            responderEmail,
            responderName,
            count: 0
          });
        }

        const current = grouped.get(responderKey);
        current.count += 1;
        if (!current.responderId && responderId) current.responderId = responderId;
        if (!current.responderEmail && responderEmail) current.responderEmail = responderEmail;
        if (!current.responderName && responderName) current.responderName = responderName;
      });

      return Array.from(grouped.values()).sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        return (a.responderEmail || a.responderId || "").localeCompare(
          b.responderEmail || b.responderId || ""
        );
      });
    },
    // Ritorna foundRows filtrato in base a selectedStudio e selectedType
    filteredRows() {
      return this.foundRows.filter(row => {
        // Filtro per Studio
        if (this.selectedStudio && row.userStudio !== this.selectedStudio) {
          return false;
        }
        // Filtro per Tipo conversazione
        if (this.selectedType && row.type !== this.selectedType) {
          return false;
        }
        return true;
      });
    }
  },
  methods: {
    normalizeType(value) {
      return String(value || "").trim().toLowerCase();
    },
    parseConversationDate(timestamp) {
      if (timestamp && typeof timestamp.toDate === "function") {
        return timestamp.toDate();
      }
      const parsed = new Date(timestamp);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    },
    async loadUsersBatch(db, userIds) {
      const ids = Array.isArray(userIds) ? userIds.filter(Boolean) : [];
      const missingIds = ids.filter(id => !Object.prototype.hasOwnProperty.call(this.usersCache, id));
      if (!missingIds.length) return;

      // "in" su documentId ha un limite per query: usiamo chunk piccoli e compatibili.
      const CHUNK_SIZE = 10;
      for (let i = 0; i < missingIds.length; i += CHUNK_SIZE) {
        const chunk = missingIds.slice(i, i + CHUNK_SIZE);
        const usersQuery = query(
          collection(db, "users"),
          where(documentId(), "in", chunk)
        );
        const usersSnap = await getDocs(usersQuery);
        const foundIds = new Set();

        usersSnap.forEach(userSnap => {
          foundIds.add(userSnap.id);
          const data = userSnap.data() || {};
          this.usersCache[userSnap.id] = {
            name: data.name || "",
            email: data.email || "",
            studio: data.studio || ""
          };
        });

        // Manteniamo anche gli utenti non trovati in cache per non riprovare ad ogni ricerca.
        chunk.forEach(id => {
          if (!foundIds.has(id)) {
            this.usersCache[id] = { name: "", email: "", studio: "" };
          }
        });
      }
    },
    async searchDocuments() {
      if (!this.startDate || !this.endDate) {
        alert("Seleziona entrambe le date per favore.");
        return;
      }
      this.isLoading = true;
      this.foundRows = [];

      try {
        const db = getFirestore();
        // 1) Recuperiamo i documenti dalla collezione assistenzatecnica
        const snap = await getDocs(collection(db, "assistenzatecnica"));

        // 2) Impostiamo inizio/fine giornata per includere tutto il giorno
        const start = new Date(`${this.startDate}T00:00:00`);
        const end = new Date(`${this.endDate}T23:59:59`);

        // 3) Estraiamo tutte le conversazioni nel range in un solo passaggio
        //    e raccogliamo userId unici da caricare in batch.
        const baseRows = [];
        const userIds = new Set();

        snap.forEach(docSnap => {
          const docData = docSnap.data() || {};
          const conversations = Array.isArray(docData.conversations) ? docData.conversations : [];
          if (!conversations.length) return;

          const userId = docData.userId || "";
          let hasMatchInRange = false;

          for (const conv of conversations) {
            const convDate = this.parseConversationDate(conv.timestamp);
            if (!convDate) continue;
            if (convDate < start || convDate > end) continue;
            hasMatchInRange = true;

            baseRows.push({
              docId: docSnap.id,
              userId,
              userName: "",
              userEmail: "",
              userStudio: "",
              responderId: conv.userId || docData.operatorId || "",
              responderName: "",
              responderEmail: "",
              type: conv.type || "" // 'Richiesta' / 'Trainer' / ...
            });

            const responderId = conv.userId || docData.operatorId || "";
            if (responderId) {
              userIds.add(responderId);
            }
          }

          if (hasMatchInRange && userId) {
            userIds.add(userId);
          }
        });

        // 4) Carichiamo i dati utente in batch (con cache) invece di fare 1 getDoc per ticket.
        await this.loadUsersBatch(db, Array.from(userIds));

        this.foundRows = baseRows.map(row => {
          const user = this.usersCache[row.userId] || {};
          const responder = this.usersCache[row.responderId] || {};
          return {
            ...row,
            userName: user.name || "",
            userEmail: user.email || "",
            userStudio: user.studio || "",
            responderName: responder.name || "",
            responderEmail: responder.email || ""
          };
        });
      } catch (error) {
        console.error("Errore durante la ricerca:", error);
        alert("Errore durante la ricerca. Controlla la console per i dettagli.");
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.archivio-container {
  margin: 20px;
}

.date-filters {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 20px;
}

.search-buttons {
  margin-bottom: 20px;
}

.filters {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.loader {
  margin: 20px 0;
  font-weight: bold;
}

.summary-box {
  margin: 12px 0;
  padding: 12px;
  border: 1px solid #cfe3ff;
  background-color: #f5f9ff;
}

.operators-box {
  margin: 12px 0 20px;
}

.operators-box h3 {
  margin: 0 0 8px;
}

table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
}

table th,
table td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
}

table th {
  background-color: #f1f1f1;
}
</style>
